import React, { useState, useEffect, useCallback } from 'react'
import Swal from 'sweetalert2'
import {
  RefreshCw,
  Calendar,
  ChevronDown,
  ChevronRight,
  Search,
  Filter,
  X,
  Eye,
  CheckCircle2,
  RotateCcw,
  Car,
  Wrench,
  Database,
  Users,
  Layers,
  AlertTriangle,
  FileText,
  Settings,
  Plus,
  GitCompare,
  Check,
  XCircle,
} from 'lucide-react'
import styles from './EntityLogs.module.scss'
import { config } from '../../config'

const MONITOR_API_URL = config.MONITOR_API_URL

type EntityType = 'cars' | 'modifications' | 'models' | 'generations' | 'dealers'
type StatusType = 'all' | 'published' | 'failed' | 'not_processed' | 'retrying' | 'resolved'
type ErrorType = 'all' | 'MISSING_DEALER' | 'MISSING_MODIFICATION' | 'MISSING_MODEL' | 'API_ERROR' | 'UNKNOWN'
type DbCheckStatusType = 'all' | 'verified' | 'mismatch' | 'missing'

interface Entity {
  _id: string
  _index: string
  _source: Record<string, unknown>
}

interface AggData {
  total: number
  published: number
  failed: number
  not_processed: number
  retrying: number
  resolved: number
  db_verified: number
  db_mismatch: number
  db_missing: number
}

interface Filter {
  type: string
  field?: string
  operator?: string
  value: string
  label: string
}

type FilterOperator = 'eq' | 'neq' | 'contains' | 'starts' | 'ends' | 'gt' | 'lt' | 'exists' | 'notexists'

const FILTER_OPERATORS: { value: FilterOperator; label: string; symbol: string }[] = [
  { value: 'eq', label: 'Equals', symbol: '=' },
  { value: 'neq', label: 'Not Equals', symbol: '≠' },
  { value: 'contains', label: 'Contains', symbol: '∋' },
  { value: 'starts', label: 'Starts With', symbol: '^' },
  { value: 'ends', label: 'Ends With', symbol: '$' },
  { value: 'gt', label: 'Greater Than', symbol: '>' },
  { value: 'lt', label: 'Less Than', symbol: '<' },
  { value: 'exists', label: 'Exists', symbol: '∃' },
  { value: 'notexists', label: 'Not Exists', symbol: '∄' },
]

const SEARCHABLE_FIELDS: Record<EntityType, { value: string; label: string }[]> = {
  cars: [
    { value: '_id', label: 'ID' },
    { value: 'correlation_id', label: 'Correlation ID' },
    { value: 'dealer_id', label: 'Dealer ID' },
    { value: 'vin', label: 'VIN' },
    { value: 'stock', label: 'Stock' },
    { value: 'make', label: 'Make' },
    { value: 'model', label: 'Model' },
    { value: 'year', label: 'Year' },
    { value: 'price', label: 'Price' },
    { value: 'http_legacy_error_ref_id', label: 'Error Ref ID' },
  ],
  modifications: [
    { value: '_id', label: 'ID' },
    { value: 'name', label: 'Name' },
    { value: 'model_id', label: 'Model ID' },
    { value: 'http_legacy_error_ref_id', label: 'Error Ref ID' },
  ],
  models: [
    { value: '_id', label: 'ID' },
    { value: 'name', label: 'Name' },
    { value: 'make_id', label: 'Make ID' },
    { value: 'http_legacy_error_ref_id', label: 'Error Ref ID' },
  ],
  generations: [
    { value: '_id', label: 'ID' },
    { value: 'name', label: 'Name' },
    { value: 'model_id', label: 'Model ID' },
    { value: 'http_legacy_error_ref_id', label: 'Error Ref ID' },
  ],
  dealers: [
    { value: '_id', label: 'ID' },
    { value: 'name', label: 'Name' },
    { value: 'city', label: 'City' },
    { value: 'state', label: 'State' },
  ],
}

const ENTITY_OPTIONS: { value: EntityType; label: string; icon: React.ReactNode }[] = [
  { value: 'cars', label: 'Cars', icon: <Car size={14} /> },
  { value: 'modifications', label: 'Modifications', icon: <Wrench size={14} /> },
  { value: 'models', label: 'Models', icon: <Database size={14} /> },
  { value: 'generations', label: 'Generations', icon: <Layers size={14} /> },
  { value: 'dealers', label: 'Dealers', icon: <Users size={14} /> },
]

const STATUS_OPTIONS: { value: StatusType; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'published', label: 'Published' },
  { value: 'failed', label: 'Failed' },
  { value: 'not_processed', label: 'Pending' },
  { value: 'retrying', label: 'Retrying' },
  { value: 'resolved', label: 'Resolved' },
]

const ERROR_OPTIONS: { value: ErrorType; label: string }[] = [
  { value: 'all', label: 'All Errors' },
  { value: 'MISSING_DEALER', label: 'Missing Dealer' },
  { value: 'MISSING_MODIFICATION', label: 'Missing Modification' },
  { value: 'MISSING_MODEL', label: 'Missing Model' },
  { value: 'API_ERROR', label: 'API Error' },
  { value: 'UNKNOWN', label: 'Unknown' },
]

const DB_CHECK_STATUS_OPTIONS: { value: DbCheckStatusType; label: string }[] = [
  { value: 'all', label: 'All DB Status' },
  { value: 'verified', label: 'Verified' },
  { value: 'mismatch', label: 'Mismatch' },
  { value: 'missing', label: 'Missing' },
]

const COLUMN_CONFIGS: Record<EntityType, { key: string; label: string }[]> = {
  cars: [
    { key: '_id', label: 'ID' },
    { key: 'correlation_id', label: 'Correlation ID' },
    { key: 'dealer_id', label: 'Dealer ID' },
    { key: 'vin', label: 'VIN' },
    { key: 'stock', label: 'Stock' },
    { key: 'make_original', label: 'Make' },
    { key: 'model_original', label: 'Model' },
    { key: 'trim_original', label: 'Trim' },
    { key: 'year', label: 'Year' },
    { key: 'price', label: 'Price' },
    { key: 'http_legacy_status', label: 'API Status' },
    { key: 'db_check_status', label: 'DB Status' },
    { key: 'http_legacy_error_type', label: 'Error Type' },
    { key: 'http_legacy_resolution_status', label: 'Resolution' },
  ],
  modifications: [
    { key: '_id', label: 'ID' },
    { key: 'name', label: 'Name' },
    { key: 'model_id', label: 'Model ID' },
    { key: 'http_legacy_status', label: 'Status' },
    { key: 'http_legacy_error_type', label: 'Error Type' },
    { key: 'http_legacy_resolution_status', label: 'Resolution' },
  ],
  models: [
    { key: '_id', label: 'ID' },
    { key: 'name', label: 'Name' },
    { key: 'make_id', label: 'Make ID' },
    { key: 'http_legacy_status', label: 'Status' },
    { key: 'http_legacy_error_type', label: 'Error Type' },
    { key: 'http_legacy_resolution_status', label: 'Resolution' },
  ],
  generations: [
    { key: '_id', label: 'ID' },
    { key: 'name', label: 'Name' },
    { key: 'model_id', label: 'Model ID' },
    { key: 'http_legacy_status', label: 'Status' },
    { key: 'http_legacy_error_type', label: 'Error Type' },
    { key: 'http_legacy_resolution_status', label: 'Resolution' },
  ],
  dealers: [
    { key: '_id', label: 'ID' },
    { key: 'name', label: 'Name' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'http_legacy_status', label: 'Status' },
    { key: 'http_legacy_error_type', label: 'Error Type' },
  ],
}

const EntityLogs: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0])
  const [entityType, setEntityType] = useState<EntityType>('cars')
  const [statusFilter, setStatusFilter] = useState<StatusType>('all')
  const [errorFilter, setErrorFilter] = useState<ErrorType>('all')
  const [dbCheckFilter, setDbCheckFilter] = useState<DbCheckStatusType>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [entities, setEntities] = useState<Entity[]>([])
  const [aggData, setAggData] = useState<AggData>({
    total: 0,
    published: 0,
    failed: 0,
    not_processed: 0,
    retrying: 0,
    resolved: 0,
    db_verified: 0,
    db_mismatch: 0,
    db_missing: 0,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [totalHits, setTotalHits] = useState(0)
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null)
  const [activeFilters, setActiveFilters] = useState<Filter[]>([])
  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    '_id',
    'http_legacy_status',
    'http_legacy_error_type',
  ])
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [showColumnPicker, setShowColumnPicker] = useState(false)
  const [filterField, setFilterField] = useState<string>('')
  const [filterOperator, setFilterOperator] = useState<FilterOperator>('contains')
  const [filterValue, setFilterValue] = useState<string>('')
  const [compareEntity, setCompareEntity] = useState<Entity | null>(null)
  const [compareData, setCompareData] = useState<{
    entity: Record<string, unknown>
    database: Record<string, unknown> | null
    comparison: Record<string, unknown> | null
  } | null>(null)
  const [compareLoading, setCompareLoading] = useState(false)
  const pageSize = 25

  const getIndexPattern = useCallback((date: string, entity: EntityType): string => {
    const d = new Date(date)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const entityMap: Record<EntityType, string> = {
      cars: 'car',
      modifications: 'modification',
      models: 'model',
      generations: 'generation',
      dealers: 'dealer',
    }
    return `feed-entity-${entityMap[entity]}-${year}${month}${day}`
  }, [])

  const fetchEntities = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const indexPattern = getIndexPattern(selectedDate, entityType)

      const params = new URLSearchParams({
        index: indexPattern,
        page: page.toString(),
        size: pageSize.toString(),
      })

      if (statusFilter !== 'all') params.append('status', statusFilter)
      if (errorFilter !== 'all') params.append('error_type', errorFilter)
      if (dbCheckFilter !== 'all') params.append('db_check_status', dbCheckFilter)
      if (searchQuery) params.append('q', searchQuery)

      const fieldFilters = activeFilters.filter((f) => f.type === 'field')
      if (fieldFilters.length > 0) {
        params.append(
          'filters',
          JSON.stringify(
            fieldFilters.map((f) => ({
              field: f.field,
              operator: f.operator,
              value: f.value,
            })),
          ),
        )
      }

      const response = await fetch(`${MONITOR_API_URL}/entities?${params}`)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        if (response.status === 404 || errorData.error?.includes('index_not_found')) {
          setEntities([])
          setTotalHits(0)
          setAggData({
            total: 0,
            published: 0,
            failed: 0,
            not_processed: 0,
            retrying: 0,
            resolved: 0,
            db_verified: 0,
            db_mismatch: 0,
            db_missing: 0,
          })
          setError(`No data found for ${selectedDate}. Try selecting a different date.`)
          return
        }
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      const data = await response.json()

      setEntities(Array.isArray(data.hits) ? data.hits : [])
      setTotalHits(typeof data.total === 'number' ? data.total : 0)
      setAggData({
        total: data.aggregations?.total ?? data.total ?? 0,
        published: data.aggregations?.published ?? 0,
        failed: data.aggregations?.failed ?? 0,
        not_processed: data.aggregations?.not_processed ?? 0,
        retrying: data.aggregations?.retrying ?? 0,
        resolved: data.aggregations?.resolved ?? 0,
        db_verified: data.aggregations?.db_verified ?? 0,
        db_mismatch: data.aggregations?.db_mismatch ?? 0,
        db_missing: data.aggregations?.db_missing ?? 0,
      })

      if (!data.hits || data.hits.length === 0) {
        setError(`No entities found for ${selectedDate}. Try selecting a different date or adjusting filters.`)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch entities')
      setEntities([])
      setTotalHits(0)
      setAggData({
        total: 0,
        published: 0,
        failed: 0,
        not_processed: 0,
        retrying: 0,
        resolved: 0,
        db_verified: 0,
        db_mismatch: 0,
        db_missing: 0,
      })
    } finally {
      setLoading(false)
    }
  }, [
    selectedDate,
    entityType,
    statusFilter,
    errorFilter,
    dbCheckFilter,
    searchQuery,
    page,
    activeFilters,
    getIndexPattern,
  ])

  useEffect(() => {
    fetchEntities()
  }, [fetchEntities])

  useEffect(() => {
    const defaultCols = COLUMN_CONFIGS[entityType].slice(0, 6).map((c) => c.key)
    setVisibleColumns(defaultCols)
    setFilterField('')
    setFilterValue('')
  }, [entityType])

  const handleAddFilter = () => {
    const newFilters: Filter[] = [...activeFilters.filter((f) => f.type === 'field')]
    if (statusFilter !== 'all') {
      newFilters.push({ type: 'status', value: statusFilter, label: `Status: ${statusFilter}` })
    }
    if (errorFilter !== 'all') {
      newFilters.push({ type: 'error', value: errorFilter, label: `Error: ${errorFilter}` })
    }
    setActiveFilters(newFilters)
    setPage(1)
  }

  const handleAddFieldFilter = () => {
    if (!filterField) return
    if (filterOperator !== 'exists' && filterOperator !== 'notexists' && !filterValue.trim()) return

    const fieldLabel = SEARCHABLE_FIELDS[entityType].find((f) => f.value === filterField)?.label || filterField
    const operatorInfo = FILTER_OPERATORS.find((o) => o.value === filterOperator)
    const displayValue = filterOperator === 'exists' || filterOperator === 'notexists' ? '' : `: ${filterValue}`

    const newFilter: Filter = {
      type: 'field',
      field: filterField,
      operator: filterOperator,
      value: filterValue,
      label: `${fieldLabel} ${operatorInfo?.symbol || '='}${displayValue}`,
    }

    setActiveFilters([...activeFilters, newFilter])
    setFilterField('')
    setFilterValue('')
    setPage(1)
  }

  const handleRemoveFilter = (index: number) => {
    const filter = activeFilters[index]
    if (filter.type === 'status') setStatusFilter('all')
    if (filter.type === 'error') setErrorFilter('all')
    setActiveFilters(activeFilters.filter((_, i) => i !== index))
    setPage(1)
  }

  const handleClearFilters = () => {
    setStatusFilter('all')
    setErrorFilter('all')
    setDbCheckFilter('all')
    setSearchQuery('')
    setActiveFilters([])
    setFilterField('')
    setFilterValue('')
    setPage(1)
  }

  const handleMarkResolved = async (entity: Entity) => {
    const result = await Swal.fire({
      title: 'Mark as Resolved?',
      text: `This will mark entity ${entity._id} as resolved.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#22c55e',
      cancelButtonColor: '#27272a',
      confirmButtonText: 'Yes, mark resolved',
      background: '#0f0f11',
      color: '#fafafa',
    })

    if (result.isConfirmed) {
      try {
        // Mark single entity as resolved by document ID
        const response = await fetch(`${MONITOR_API_URL}/mark-resolved-single`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            entity_id: entity._id,
            index_name: entity._index,
          }),
        })

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.error || 'Failed to mark as resolved')
        }

        await Swal.fire({
          title: 'Marked!',
          text: 'Entity has been marked as resolved.',
          icon: 'success',
          background: '#0f0f11',
          color: '#fafafa',
          confirmButtonColor: '#22c55e',
        })

        fetchEntities()
      } catch (err) {
        Swal.fire({
          title: 'Error',
          text: err instanceof Error ? err.message : 'Failed to mark as resolved',
          icon: 'error',
          background: '#0f0f11',
          color: '#fafafa',
        })
      }
    }
  }

  const handleRetryEntity = async (entity: Entity) => {
    const result = await Swal.fire({
      title: 'Retry Entity?',
      text: `This will queue entity ${entity._id} for retry processing.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3b82f6',
      cancelButtonColor: '#27272a',
      confirmButtonText: 'Yes, retry',
      background: '#0f0f11',
      color: '#fafafa',
    })

    if (result.isConfirmed) {
      try {
        // Direct single entity retry - sends directly to http-legacy-notifications queue
        const response = await fetch(`${MONITOR_API_URL}/retry-single`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            entity_id: entity._id,
            index_name: entity._index,
          }),
        })

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.error || 'Failed to queue retry')
        }

        await Swal.fire({
          title: 'Queued!',
          text: 'Entity has been queued for retry.',
          icon: 'success',
          background: '#0f0f11',
          color: '#fafafa',
          confirmButtonColor: '#3b82f6',
        })

        fetchEntities()
      } catch (err) {
        Swal.fire({
          title: 'Error',
          text: err instanceof Error ? err.message : 'Failed to queue retry',
          icon: 'error',
          background: '#0f0f11',
          color: '#fafafa',
        })
      }
    }
  }

  const handleViewDetails = (entity: Entity) => {
    setSelectedEntity(entity)
  }

  const handleCompare = async (entity: Entity) => {
    setCompareEntity(entity)
    setCompareLoading(true)
    setCompareData(null)

    try {
      const response = await fetch(
        `${MONITOR_API_URL}/compare/${encodeURIComponent(entity._id)}?index=${encodeURIComponent(entity._index)}`,
      )
      const data = await response.json()
      if (response.ok) {
        setCompareData(data)
      } else {
        Swal.fire({
          title: 'Error',
          text: data.error || 'Failed to fetch comparison data',
          icon: 'error',
          background: '#0f0f11',
          color: '#fafafa',
        })
        setCompareEntity(null)
      }
    } catch (err) {
      Swal.fire({
        title: 'Error',
        text: err instanceof Error ? err.message : 'Failed to fetch comparison data',
        icon: 'error',
        background: '#0f0f11',
        color: '#fafafa',
      })
      setCompareEntity(null)
    } finally {
      setCompareLoading(false)
    }
  }

  const renderMatchIcon = (match: boolean | null | undefined) => {
    if (match === true) return <Check size={14} className={styles.matchIcon} />
    if (match === false) return <XCircle size={14} className={styles.mismatchIcon} />
    return <span className={styles.naIcon}>-</span>
  }

  const getStatusClass = (status: string | undefined): string => {
    if (!status) return styles.statusPending
    const map: Record<string, string> = {
      published: styles.statusPublished,
      failed: styles.statusFailed,
      not_processed: styles.statusPending,
      retrying: styles.statusRetrying,
      resolved: styles.statusResolved,
    }
    return map[status] || styles.statusPending
  }

  const getDbCheckStatusClass = (status: string | undefined): string => {
    if (!status) return styles.statusPending
    const map: Record<string, string> = {
      verified: styles.statusVerified,
      mismatch: styles.statusMismatch,
      missing: styles.statusMissing,
    }
    return map[status] || styles.statusPending
  }

  const getCellValue = (entity: Entity, key: string): string => {
    if (key === '_id') return entity._id
    if (key === '_index') return entity._index
    const value = entity._source[key]
    if (value === null || value === undefined) return '-'
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  }

  const toggleColumn = (key: string) => {
    if (visibleColumns.includes(key)) {
      setVisibleColumns(visibleColumns.filter((c) => c !== key))
    } else {
      setVisibleColumns([...visibleColumns, key])
    }
  }

  const totalPages = Math.ceil(totalHits / pageSize)
  const total = aggData.total || 0

  // Progress bar data
  const statusSegments = [
    { key: 'published', value: aggData.published, color: '#22c55e', label: 'Published' },
    { key: 'failed', value: aggData.failed, color: '#ef4444', label: 'Failed' },
    { key: 'pending', value: aggData.not_processed, color: '#f59e0b', label: 'Pending' },
    { key: 'retrying', value: aggData.retrying, color: '#3b82f6', label: 'Retrying' },
    { key: 'resolved', value: aggData.resolved, color: '#a855f7', label: 'Resolved' },
  ].filter((s) => s.value > 0)

  const getEntityIcon = () => {
    const found = ENTITY_OPTIONS.find((e) => e.value === entityType)
    return found?.icon || <Database size={14} />
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.titleGroup}>
            <div className={styles.logoIcon}>
              <FileText size={18} />
            </div>
            <h1 className={styles.title}>Entity Logs</h1>
          </div>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.refreshBtn} onClick={fetchEntities} disabled={loading}>
            <RefreshCw size={14} className={loading ? styles.spinning : ''} />
            <span>Refresh</span>
          </button>
        </div>
      </header>

      {/* Error Banner */}
      {error && (
        <div className={styles.errorBanner}>
          <AlertTriangle size={14} />
          <span>{error}</span>
        </div>
      )}

      {/* Status Overview */}
      <div className={styles.statusOverview}>
        <div className={styles.totalCount}>
          <span className={styles.totalValue}>{total.toLocaleString()}</span>
          <span className={styles.totalLabel}>Total Entities</span>
        </div>

        <div className={styles.progressSection}>
          {total > 0 ? (
            <>
              <div className={styles.progressBar}>
                {statusSegments.map((seg) => (
                  <div
                    key={seg.key}
                    className={styles.progressSegment}
                    style={{
                      width: `${(seg.value / total) * 100}%`,
                      backgroundColor: seg.color,
                    }}
                    title={`${seg.label}: ${seg.value.toLocaleString()} (${((seg.value / total) * 100).toFixed(1)}%)`}
                  />
                ))}
              </div>
              <div className={styles.progressLegend}>
                {statusSegments.map((seg) => (
                  <div key={seg.key} className={styles.legendItem}>
                    <span className={styles.legendDot} style={{ backgroundColor: seg.color }} />
                    <span className={styles.legendLabel}>{seg.label}</span>
                    <span className={styles.legendValue}>{seg.value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className={styles.noData}>No data available</div>
          )}
        </div>
      </div>

      {/* Filters Section */}
      <div className={styles.filtersCard}>
        <div className={styles.primaryFilters}>
          {/* Date */}
          <div className={styles.filterItem}>
            <Calendar size={14} className={styles.filterIcon} />
            <input
              type="date"
              className={styles.dateInput}
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value)
                setPage(1)
              }}
            />
          </div>

          {/* Entity Type */}
          <div className={styles.filterItem}>
            {getEntityIcon()}
            <select
              className={styles.filterSelect}
              value={entityType}
              onChange={(e) => {
                setEntityType(e.target.value as EntityType)
                setPage(1)
              }}>
              {ENTITY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className={styles.selectChevron} />
          </div>

          {/* Status */}
          <div className={styles.filterItem}>
            <select
              className={styles.filterSelect}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusType)}>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className={styles.selectChevron} />
          </div>

          {/* Error Type */}
          <div className={styles.filterItem}>
            <select
              className={styles.filterSelect}
              value={errorFilter}
              onChange={(e) => setErrorFilter(e.target.value as ErrorType)}>
              {ERROR_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className={styles.selectChevron} />
          </div>

          {/* DB Check Status */}
          <div className={styles.filterItem}>
            <Database size={14} className={styles.filterIcon} />
            <select
              className={styles.filterSelect}
              value={dbCheckFilter}
              onChange={(e) => setDbCheckFilter(e.target.value as DbCheckStatusType)}>
              {DB_CHECK_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className={styles.selectChevron} />
          </div>

          {/* Search */}
          <div className={styles.searchItem}>
            <Search size={14} className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search by ID, VIN..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddFilter()}
            />
          </div>

          <button className={styles.applyBtn} onClick={handleAddFilter}>
            Apply
          </button>
          <button className={styles.clearBtn} onClick={handleClearFilters}>
            Clear
          </button>

          <button
            className={`${styles.toggleBtn} ${showAdvancedFilters ? styles.active : ''}`}
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}>
            <Filter size={14} />
            <span>Advanced</span>
            {showAdvancedFilters ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        </div>

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <div className={styles.advancedFilters}>
            <div className={styles.advancedRow}>
              <select
                className={styles.fieldSelect}
                value={filterField}
                onChange={(e) => setFilterField(e.target.value)}>
                <option value="">Select Field</option>
                {SEARCHABLE_FIELDS[entityType].map((field) => (
                  <option key={field.value} value={field.value}>
                    {field.label}
                  </option>
                ))}
              </select>

              <select
                className={styles.operatorSelect}
                value={filterOperator}
                onChange={(e) => setFilterOperator(e.target.value as FilterOperator)}>
                {FILTER_OPERATORS.map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.symbol} {op.label}
                  </option>
                ))}
              </select>

              {filterOperator !== 'exists' && filterOperator !== 'notexists' && (
                <input
                  type="text"
                  className={styles.valueInput}
                  placeholder="Enter value..."
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddFieldFilter()}
                />
              )}

              <button
                className={styles.addFilterBtn}
                onClick={handleAddFieldFilter}
                disabled={
                  !filterField || (filterOperator !== 'exists' && filterOperator !== 'notexists' && !filterValue.trim())
                }>
                <Plus size={14} />
                <span>Add</span>
              </button>
            </div>
          </div>
        )}

        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <div className={styles.activeFilters}>
            {activeFilters.map((filter, i) => (
              <span key={i} className={styles.filterChip}>
                <span>{filter.label}</span>
                <button className={styles.chipRemove} onClick={() => handleRemoveFilter(i)}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Table Section */}
      <div className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <div className={styles.tableTitle}>
            {getEntityIcon()}
            <span>{ENTITY_OPTIONS.find((e) => e.value === entityType)?.label} Entities</span>
          </div>
          <div className={styles.tableActions}>
            <span className={styles.tableCount}>{totalHits.toLocaleString()} results</span>
            <div className={styles.columnPicker}>
              <button className={styles.columnBtn} onClick={() => setShowColumnPicker(!showColumnPicker)}>
                <Settings size={14} />
                <span>Columns</span>
              </button>
              {showColumnPicker && (
                <div className={styles.columnDropdown}>
                  {COLUMN_CONFIGS[entityType].map((col) => (
                    <label key={col.key} className={styles.columnOption}>
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(col.key)}
                        onChange={() => toggleColumn(col.key)}
                      />
                      <span>{col.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className={styles.loading}>
            <RefreshCw size={24} className={styles.spinning} />
            <span>Loading entities...</span>
          </div>
        ) : entities.length === 0 ? (
          <div className={styles.emptyState}>
            <Database size={48} />
            <h3>No entities found</h3>
            <p>Try adjusting your filters or selecting a different date.</p>
          </div>
        ) : (
          <>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    {COLUMN_CONFIGS[entityType]
                      .filter((col) => visibleColumns.includes(col.key))
                      .map((col) => (
                        <th key={col.key}>{col.label}</th>
                      ))}
                    <th className={styles.actionsHeader}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entities.map((entity) => (
                    <tr key={entity._id}>
                      {COLUMN_CONFIGS[entityType]
                        .filter((col) => visibleColumns.includes(col.key))
                        .map((col) => (
                          <td key={col.key}>
                            {col.key === 'http_legacy_status' ? (
                              <span
                                className={`${styles.statusBadge} ${getStatusClass(
                                  entity._source.http_legacy_status as string,
                                )}`}>
                                {(entity._source.http_legacy_status as string) || 'pending'}
                              </span>
                            ) : col.key === 'db_check_status' ? (
                              entity._source.db_check_status ? (
                                <span
                                  className={`${styles.statusBadge} ${getDbCheckStatusClass(
                                    entity._source.db_check_status as string,
                                  )}`}>
                                  {entity._source.db_check_status as string}
                                </span>
                              ) : (
                                <span className={styles.muted}>-</span>
                              )
                            ) : col.key === 'http_legacy_error_type' ? (
                              entity._source.http_legacy_error_type ? (
                                <span className={styles.errorBadge}>
                                  {(entity._source.http_legacy_error_type as string).replace(/_/g, ' ')}
                                </span>
                              ) : (
                                <span className={styles.muted}>-</span>
                              )
                            ) : col.key === 'price' ? (
                              <span className={styles.cellText}>
                                ${Number(entity._source.price || 0).toLocaleString()}
                              </span>
                            ) : col.key === '_id' ? (
                              <code className={styles.codeCell}>{getCellValue(entity, col.key)}</code>
                            ) : (
                              <span className={styles.cellText}>{getCellValue(entity, col.key)}</span>
                            )}
                          </td>
                        ))}
                      <td>
                        <div className={styles.rowActions}>
                          <button
                            className={styles.iconBtn}
                            onClick={() => handleViewDetails(entity)}
                            title="View Details">
                            <Eye size={14} />
                          </button>
                          {entity._source.db_check_status && (
                            <button
                              className={`${styles.iconBtn} ${styles.compare}`}
                              onClick={() => handleCompare(entity)}
                              title="Compare with DB">
                              <GitCompare size={14} />
                            </button>
                          )}
                          {entity._source.http_legacy_status === 'failed' && (
                            <>
                              <button
                                className={`${styles.iconBtn} ${styles.success}`}
                                onClick={() => handleMarkResolved(entity)}
                                title="Mark Resolved">
                                <CheckCircle2 size={14} />
                              </button>
                              <button
                                className={`${styles.iconBtn} ${styles.info}`}
                                onClick={() => handleRetryEntity(entity)}
                                title="Retry">
                                <RotateCcw size={14} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className={styles.pagination}>
                <span className={styles.pageInfo}>
                  {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalHits)} of {totalHits.toLocaleString()}
                </span>
                <div className={styles.pageButtons}>
                  <button className={styles.pageBtn} disabled={page === 1} onClick={() => setPage(1)}>
                    First
                  </button>
                  <button className={styles.pageBtn} disabled={page === 1} onClick={() => setPage(page - 1)}>
                    Prev
                  </button>
                  <span className={styles.pageNumber}>Page {page}</span>
                  <button className={styles.pageBtn} disabled={page === totalPages} onClick={() => setPage(page + 1)}>
                    Next
                  </button>
                  <button className={styles.pageBtn} disabled={page === totalPages} onClick={() => setPage(totalPages)}>
                    Last
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Panel (Slide-in) */}
      {selectedEntity && (
        <>
          <div className={styles.panelOverlay} onClick={() => setSelectedEntity(null)} />
          <div className={styles.detailPanel}>
            <div className={styles.panelHeader}>
              <div className={styles.panelTitle}>
                <FileText size={16} />
                <span>Entity Details</span>
              </div>
              <button className={styles.panelClose} onClick={() => setSelectedEntity(null)}>
                <X size={18} />
              </button>
            </div>

            <div className={styles.panelBody}>
              <div className={styles.detailSection}>
                <h4 className={styles.sectionTitle}>Overview</h4>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>ID</span>
                    <code className={styles.detailCode}>{selectedEntity._id}</code>
                  </div>
                  {selectedEntity._source.correlation_id && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Correlation ID</span>
                      <code className={styles.detailCode}>{selectedEntity._source.correlation_id as string}</code>
                    </div>
                  )}
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Index</span>
                    <span className={styles.detailValue}>{selectedEntity._index}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Status</span>
                    <span
                      className={`${styles.statusBadge} ${getStatusClass(
                        selectedEntity._source.http_legacy_status as string,
                      )}`}>
                      {(selectedEntity._source.http_legacy_status as string) || 'pending'}
                    </span>
                  </div>
                  {selectedEntity._source.http_legacy_error_type && (
                    <>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Error Type</span>
                        <span className={styles.errorBadge}>
                          {selectedEntity._source.http_legacy_error_type as string}
                        </span>
                      </div>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Error Ref ID</span>
                        <span className={styles.detailValue}>
                          {(selectedEntity._source.http_legacy_error_ref_id as string) || '-'}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {selectedEntity._source.http_legacy_message && (
                <div className={styles.detailSection}>
                  <h4 className={styles.sectionTitle}>Error Message</h4>
                  <div className={styles.messageBox}>{selectedEntity._source.http_legacy_message as string}</div>
                </div>
              )}

              <div className={styles.detailSection}>
                <h4 className={styles.sectionTitle}>Raw Document</h4>
                <pre className={styles.jsonView}>{JSON.stringify(selectedEntity._source, null, 2)}</pre>
              </div>
            </div>

            <div className={styles.panelFooter}>
              {selectedEntity._source.http_legacy_status === 'failed' && (
                <>
                  <button
                    className={`${styles.panelBtn} ${styles.success}`}
                    onClick={() => {
                      handleMarkResolved(selectedEntity)
                      setSelectedEntity(null)
                    }}>
                    <CheckCircle2 size={14} />
                    <span>Mark Resolved</span>
                  </button>
                  <button
                    className={`${styles.panelBtn} ${styles.info}`}
                    onClick={() => {
                      handleRetryEntity(selectedEntity)
                      setSelectedEntity(null)
                    }}>
                    <RotateCcw size={14} />
                    <span>Retry Now</span>
                  </button>
                </>
              )}
              <button className={`${styles.panelBtn} ${styles.secondary}`} onClick={() => setSelectedEntity(null)}>
                Close
              </button>
            </div>
          </div>
        </>
      )}

      {/* Compare Panel */}
      {compareEntity && (
        <>
          <div className={styles.panelOverlay} onClick={() => setCompareEntity(null)} />
          <div className={`${styles.detailPanel} ${styles.comparePanel}`}>
            <div className={styles.panelHeader}>
              <div className={styles.panelTitle}>
                <GitCompare size={16} />
                <span>DB Comparison</span>
                {compareData?.comparison && (
                  <span
                    className={`${styles.compareStatusBadge} ${
                      compareData.entity.db_check_status === 'verified'
                        ? styles.verified
                        : compareData.entity.db_check_status === 'mismatch'
                        ? styles.mismatch
                        : styles.missing
                    }`}>
                    {(compareData.entity.db_check_status as string) || 'unknown'}
                  </span>
                )}
              </div>
              <button className={styles.panelClose} onClick={() => setCompareEntity(null)}>
                <X size={18} />
              </button>
            </div>

            <div className={styles.panelBody}>
              {compareLoading ? (
                <div className={styles.loading}>
                  <RefreshCw size={24} className={styles.spinning} />
                  <span>Loading comparison data...</span>
                </div>
              ) : compareData ? (
                <>
                  <div className={styles.detailSection}>
                    <h4 className={styles.sectionTitle}>Entity Info</h4>
                    <div className={styles.compareInfo}>
                      <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Correlation ID</span>
                        <code className={styles.infoValue}>{(compareData.entity.correlation_id as string) || '-'}</code>
                      </div>
                      <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>VIN</span>
                        <code className={styles.infoValue}>{(compareData.entity.vin as string) || '-'}</code>
                      </div>
                      <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>DB Record ID</span>
                        <code className={styles.infoValue}>
                          {compareData.comparison?.db_record_id != null
                            ? String(compareData.comparison.db_record_id)
                            : 'Not found'}
                        </code>
                      </div>
                      <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Lookup Method</span>
                        <span className={styles.infoValue}>
                          {(compareData.comparison?.lookup_method as string) || '-'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className={styles.detailSection}>
                    <h4 className={styles.sectionTitle}>Field Comparison</h4>
                    <div className={styles.compareTable}>
                      <div className={styles.compareRow}>
                        <div className={styles.compareHeader}>Field</div>
                        <div className={styles.compareHeader}>Entity (ES)</div>
                        <div className={styles.compareHeader}>Database</div>
                        <div className={styles.compareHeader}>Match</div>
                      </div>

                      <div className={styles.compareRow}>
                        <div className={styles.compareCell}>Price</div>
                        <div className={styles.compareCell}>
                          <code>${Number(compareData.entity.price || 0).toLocaleString()}</code>
                        </div>
                        <div className={styles.compareCell}>
                          <code>
                            {compareData.database?.price != null
                              ? `$${Number(compareData.database.price).toLocaleString()}`
                              : '-'}
                          </code>
                        </div>
                        <div className={styles.compareCell}>
                          {renderMatchIcon(compareData.comparison?.price_match as boolean | null)}
                        </div>
                      </div>

                      <div className={styles.compareRow}>
                        <div className={styles.compareCell}>Year</div>
                        <div className={styles.compareCell}>
                          <code>{compareData.entity.year || '-'}</code>
                        </div>
                        <div className={styles.compareCell}>
                          <code>{compareData.database?.year || '-'}</code>
                        </div>
                        <div className={styles.compareCell}>
                          {renderMatchIcon(compareData.comparison?.year_match as boolean | null)}
                        </div>
                      </div>

                      <div className={styles.compareRow}>
                        <div className={styles.compareCell}>Mileage</div>
                        <div className={styles.compareCell}>
                          <code>{Number(compareData.entity.mileage || 0).toLocaleString()}</code>
                        </div>
                        <div className={styles.compareCell}>
                          <code>
                            {compareData.database?.mileage != null
                              ? Number(compareData.database.mileage).toLocaleString()
                              : '-'}
                          </code>
                        </div>
                        <div className={styles.compareCell}>
                          {renderMatchIcon(compareData.comparison?.mileage_match as boolean | null)}
                        </div>
                      </div>

                      <div className={styles.compareRow}>
                        <div className={styles.compareCell}>Dealer ID</div>
                        <div className={styles.compareCell}>
                          <code>{compareData.entity.dealer_id || '-'}</code>
                        </div>
                        <div className={styles.compareCell}>
                          <code>{compareData.database?.dealer_id ?? compareData.database?.dealer ?? '-'}</code>
                        </div>
                        <div className={styles.compareCell}>
                          {renderMatchIcon(compareData.comparison?.dealer_match as boolean | null)}
                        </div>
                      </div>

                      <div className={styles.compareRow}>
                        <div className={styles.compareCell}>Stock</div>
                        <div className={styles.compareCell}>
                          <code>{compareData.entity.stock || '-'}</code>
                        </div>
                        <div className={styles.compareCell}>
                          <code>{compareData.database?.stock ?? '-'}</code>
                        </div>
                        <div className={styles.compareCell}>
                          {renderMatchIcon(compareData.comparison?.stock_match as boolean | null)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {compareData.comparison?.mismatches && (compareData.comparison.mismatches as string[]).length > 0 && (
                    <div className={styles.detailSection}>
                      <h4 className={styles.sectionTitle}>Mismatch Details</h4>
                      <div className={styles.mismatchList}>
                        {(compareData.comparison.mismatches as string[]).map((m, i) => (
                          <div key={i} className={styles.mismatchItem}>
                            <XCircle size={14} />
                            <span>{m}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.emptyState}>
                  <Database size={48} />
                  <h3>No comparison data</h3>
                  <p>This entity has not been verified against the database yet.</p>
                </div>
              )}
            </div>

            <div className={styles.panelFooter}>
              <button className={`${styles.panelBtn} ${styles.secondary}`} onClick={() => setCompareEntity(null)}>
                Close
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default EntityLogs
